import {useEffect} from "react";
import {Group, Select, Text, ThemeIcon} from "@mantine/core";
import classes from "./SettingSelectField.module.css";
import {useTranslation} from "react-i18next";
import {useLocalStorage} from "@mantine/hooks";
import {bffApi} from "../../bffApi";
import {chatApi} from "../../chatApi";
import {
    getApiLanguageCode,
    getI18nLanguageCode,
    getLangCodeFromEnum,
    LanguageCode,
    LanguageEnum,
} from "../../../../types";
import IconNorway from "../../../../assets/flag-no.svg?react";
import IconSweden from "../../../../assets/flag-se.svg?react";
import IconDenmark from "../../../../assets/flag-dk.svg?react";
import IconUnitedKingdom from "../../../../assets/flag-gb.svg?react";
import i18n from "../../../../i18n";

const flagMap = {
    nb: <IconNorway />,
    en: <IconUnitedKingdom />,
    sv: <IconSweden />,
    da: <IconDenmark />,
};

export function LanguageSelectField() {
    const user = bffApi.endpoints.getUser.useQuery();
    const settings = chatApi.endpoints.getSettings.useQuery();
    const [updateSettings] = chatApi.endpoints.updateSettings.useMutation();

    // Get default language from account if no user preference is set
    const accountLanguage = getLangCodeFromEnum(
        user.data?.spraakId as LanguageEnum
    );

    // Get the effective language: user preference -> account language -> Norwegian
    const effectiveLanguage = settings.data?.preferredLanguage
        ? getI18nLanguageCode(settings.data.preferredLanguage)
        : accountLanguage ?? LanguageCode.Norwegian;

    // Keep localStorage in sync for i18n purposes
    const [language, setLanguage] = useLocalStorage({
        key: "language",
        defaultValue: effectiveLanguage,
    });

    // Sync localStorage when settings are loaded
    useEffect(() => {
        if (settings.data?.preferredLanguage && language !== effectiveLanguage) {
            setLanguage(effectiveLanguage);
        }
    }, [settings.data?.preferredLanguage, effectiveLanguage]);

    const {t} = useTranslation();
    const handleChange = async (value: string | null) => {
        const newLanguage = value ?? LanguageCode.Norwegian;

        // Update localStorage and i18n immediately for responsive UI
        setLanguage(newLanguage);
        i18n.changeLanguage(newLanguage);

        // Persist to backend via settings API
        await updateSettings({
            preferredLanguage: getApiLanguageCode(newLanguage),
        });
    };

    return (
        <Group className={classes.outer}>
            <Text className={classes.innerItem}>{t("settings.language")}</Text>
            <Select
                className={classes.innerItem}
                leftSection={
                    <ThemeIcon
                        variant="light"
                        size="sm"
                        radius="xl"
                        color="gneis.8"
                        bg="transparent"
                    >
                        {flagMap[language as keyof typeof flagMap]}
                    </ThemeIcon>
                }
                data={[
                    {value: LanguageCode.Norwegian, label: "Norsk"},
                    {value: LanguageCode.English, label: "English"},
                    {value: LanguageCode.Swedish, label: "Svenska"},
                    {value: LanguageCode.Danish, label: "Dansk"},
                ]}
                value={language}
                onChange={handleChange}
            />
        </Group>
    );
}
